/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemSecondaryAction,
  Divider,
  Switch,
  FormControlLabel,
  Stack,
  SvgIcon,
  useTheme,
  useMediaQuery,
  Typography,
  Chip,
  Checkbox,
  MenuItem,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Publish as PublishIcon,
  DragIndicator as DragIndicatorIcon,
  ArrowLeft,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import { Quill } from 'react-quill';


// Override Quill's default video blot to render <video> instead of <iframe>
// This prevents arbitrary iframe embeds from running inside our app context
const BlockEmbed = Quill.import('blots/block/embed') as any;
class VideoBlot extends BlockEmbed {
  static create(value: string) {
    const node = super.create() as HTMLElement;
    node.setAttribute('src', value);
    node.setAttribute('controls', '');
    node.setAttribute('preload', 'metadata');
    node.setAttribute('style', 'width: 100%; aspect-ratio: 16/9; display: block; margin: 8px 0; background: #000;');
    return node;
  }
  static value(node: HTMLElement) {
    return node.getAttribute('src');
  }
}
VideoBlot.blotName = 'video';
VideoBlot.tagName = 'video';
Quill.register(VideoBlot, true);
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { TrainingClientApi } from '../../../app/services/api/compliance/training/trainingClientAPI';
import {
  TrainingModuleDetailDto,
  CreateTrainingModuleDto,
  TrainingCategoryDto,
  TrainingModuleContentDto,
  TrainingModuleQuizDto,
  TrainingQuizQuestionDto,
  TrainingQuizAnswerDto,
  UpdateTrainingModuleDto,
  TrainingFranchiseOptionDto,
} from '../../../app/services/api/compliance/training/trainingTypes';
import { trainingClient } from '../../../app/services/api/compliance/training';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import { UserContext } from '../../../components/shared/useUser';
import { Section } from '../../../components/ui/Section';

enum QuestionType {
  Text = 0,
  Number = 1,
  Boolean = 2,
  SingleChoice = 3,
  MultipleChoice = 4,
  Photo = 5,
  Signature = 6,
  Date = 7,
  Rating = 8,
}
interface TrainingModuleFormState {
  id?: number;
  name: string;
  description: string;
  franchiseIds: number[];
  categoryId: number;
  categoryName: string;
  durationMinutes: number;
  passingScore: number;
  validityDays: number | null;
  isActive: boolean;
  content: TrainingModuleContentDto[];
  quizzes: TrainingModuleQuizDto[];
}

const TrainingModuleBuilderPage: React.FC = () => {
  const navigate = useNavigate();
  const { moduleId } = useParams<{ moduleId?: string }>();
  const theme = useTheme();
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);
  const { t } = useTranslation('training');

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [contentLoading, setContentLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contentDialogError, setContentDialogError] = useState<string | null>(null);
  const [quizDialogError, setQuizDialogError] = useState<string | null>(null);
  const [questionDialogError, setQuestionDialogError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [currentTab, setCurrentTab] = useState(0);

  const [module, setModule] = useState<Partial<TrainingModuleFormState>>({
    name: '',
    description: '',
    franchiseIds: [],
    categoryId: 0,
    categoryName: '',
    durationMinutes: 0,
    passingScore: 70,
    validityDays: 365,
    isActive: true,
    content: [],
    quizzes: [],
  });
  const [contentDialogOpen, setContentDialogOpen] = useState(false);
  const [quizDialogOpen, setQuizDialogOpen] = useState(false);
  const [questionDialogOpen, setQuestionDialogOpen] = useState(false);
  const [editingContent, setEditingContent] = useState<TrainingModuleContentDto | null>(null);
  const [editingQuiz, setEditingQuiz] = useState<TrainingModuleQuizDto | null>(null);
  const [editingQuestion, setEditingQuestion] = useState<TrainingQuizQuestionDto | null>(null);
  const [currentContentIndex, setCurrentContentIndex] = useState(-1);
  const [currentQuizIndex, setCurrentQuizIndex] = useState(-1);
  const [originalContentSize, setOriginalContentSize] = useState(0);
  const [categories, setCategories] = useState<TrainingCategoryDto[]>([]);
  const [franchises, setFranchises] = useState<TrainingFranchiseOptionDto[]>([]);
  const { selectedFranchise } = useContext(UserContext);
  const quillRef = useRef<ReactQuill>(null);

  const quillModules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ color: [] }, { background: [] }],
        ['link', 'image', 'video'],
        ['clean'],
      ],
      handlers: {
        video: () => {
          const url = window.prompt('Enter video URL (mp4, webm, etc.):');
          if (!url?.trim()) return;
          let parsed: URL;
          try {
            parsed = new URL(url.trim());
          } catch {
            return;
          }
          if (!['http:', 'https:'].includes(parsed.protocol)) {
            return;
          }
          const quill = quillRef.current?.getEditor();
          const range = quill?.getSelection(true);
          if (quill && range) {
            quill.insertEmbed(range.index, 'video', parsed.toString());
          }
        },
      },
    },
  }), []);

  useEffect(() => {
    loadFranchises();
    loadCategories();
    if (moduleId) {
      loadModule();
    }
  }, [moduleId]);

  const loadFranchises = async () => {
    try {
      const contextFranchiseId = selectedFranchise?.franchiseId;
      const data = await trainingClient.getAccessibleFranchises(contextFranchiseId);
      setFranchises(data);
      // Pre-select franchises for new modules based on context
      if (!moduleId && data.length > 0) {
        if (data.length === 1) {
          setModule(prev => ({ ...prev, franchiseIds: [data[0].id] }));
        } else if (contextFranchiseId && data.some(f => f.id === contextFranchiseId)) {
          setModule(prev => ({ ...prev, franchiseIds: [contextFranchiseId] }));
        }
      }
    } catch (err) {
      setError(t('moduleBuilder.failedToLoadFranchises'));
    }
  };

  const loadCategories = async () => {
    try {
      const data = await trainingClient.getCategories({ franchiseId: selectedFranchise?.franchiseId });
      setCategories(data);
    } catch (err) {
      setError(t('moduleBuilder.failedToLoadCategories'));
    }
  };
  const loadModule = async () => {
    try {
      setLoading(true);
      const data = await trainingClient.getModule(parseInt(moduleId!));

      if (data) {

        setModule({
          id: data.id,
          name: data.name,
          description: data.description || '',
          franchiseIds: data.franchiseIds || [],
          categoryId: data.categoryId,
          categoryName: data.categoryName || '',
          durationMinutes: data.estimatedDurationMinutes,
          passingScore: data.passingScore || 70,
          validityDays: data.validityPeriodDays,
          isActive: data.status === 1,
          content: data.content || [],
          quizzes: data.quizzes || [],
        });

      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('moduleBuilder.failedToLoadModule'));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveModule = async () => {
    if (!module.name?.trim()) {
      setError(t('moduleBuilder.moduleNameRequired'));
      return;
    }
    if (!moduleId && (!module.franchiseIds || module.franchiseIds.length === 0)) {
      setError(t('moduleBuilder.franchiseRequired'));
      return;
    }
    if (!module.categoryId || module.categoryId === 0) {
      setError(t('moduleBuilder.categoryRequired'));
      return;
    }
    try {
      setSaving(true);
      setError(null);

      if (moduleId) {
        const updateDto: UpdateTrainingModuleDto = {
          categoryId: module.categoryId || 0,
          franchiseIds: module.franchiseIds || [],
          name: module.name,
          description: module.description || null,
          estimatedDurationMinutes: module.durationMinutes || 0,
          validityPeriodDays: module.validityDays || null,
          passingScore: module.passingScore || null,

          // Sections are managed via dedicated section endpoints – do not send here.
          content: [],

          // Quizzes are managed via dedicated quiz endpoints – do not send here.
          quizzes: [],
        };

        await trainingClient.updateModule(parseInt(moduleId), updateDto);
        setSuccess(t('moduleBuilder.moduleUpdated'));
        await loadModule();
      } else {
        const createDto: CreateTrainingModuleDto = {
          categoryId: module.categoryId || 0,
          franchiseIds: module.franchiseIds || [],
          name: module.name,
          description: module.description || null,
          passingScore: module.passingScore || null,
          estimatedDurationMinutes: module.durationMinutes || 0,
          validityPeriodDays: module.validityDays || null,
          // Content and quizzes are added via dedicated endpoints after the module is created.
          content: [],
          quizzes: [],
        };

        const newId = await trainingClient.createModule(createDto);
        setSuccess(t('moduleBuilder.moduleCreated'));
        // Redirect to the edit page so content and quizzes can be added immediately.
        setTimeout(() => navigate(`/compliance/training/modules/builder/${newId}`), 800);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('moduleBuilder.failedToSave'));
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!moduleId) {
      setError('Module ID is required to publish');
      return;
    }

    if (!module.content || module.content.length === 0) {
      setError(t('moduleBuilder.publishRequiresContent', 'Please add at least one content section before publishing.'));
      return;
    }

    if (!module.quizzes || module.quizzes.length === 0) {
      setError(t('moduleBuilder.publishRequiresQuiz', 'Please add at least one quiz before publishing.'));
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await trainingClient.publishModule(parseInt(moduleId));
      setSuccess(t('moduleBuilder.modulePublished'));
      await loadModule(); // Reload to get updated status
    } catch (err) {
      setError(err instanceof Error ? err.message : t('moduleBuilder.failedToPublish'));
    } finally {
      setSaving(false);
    }
  };

  const handleAddContent = () => {
    setContentDialogError(null);
    setCurrentContentIndex(-1);
    setOriginalContentSize(0);
    setEditingContent({
      id: 0,
      title: '',
      contentType: 'text',
      contentText: '',
      contentUrl: null,
      displayOrder: module.content?.length || 0,
      estimatedMinutes: 0,
    } as unknown as TrainingModuleContentDto);
    setContentDialogOpen(true);
  };

  const handleSaveContent = async () => {
    if (!editingContent?.title.trim()) {
      setContentDialogError(t('moduleBuilder.contentTitleRequired'));
      return;
    }

    if (editingContent.contentText && editingContent.contentText.length > Math.max(10_000_000, originalContentSize)) {
      setContentDialogError(t('moduleBuilder.contentTooLarge'));
      return;
    }

    setContentDialogError(null);

    try {
      setSaving(true);
      const isExisting = editingContent.id && editingContent.id > 0;
      if (isExisting) {
        await trainingClient.updateSection(module.id!, editingContent.id, {
          title: editingContent.title,
          contentText: editingContent.contentText,
          contentUrl: editingContent.contentUrl,
          displayOrder: editingContent.displayOrder,
          estimatedMinutes: editingContent.estimatedMinutes,
        });
      } else {
        await trainingClient.createSection(module.id!, {
          title: editingContent.title,
          contentType: editingContent.contentType,
          contentText: editingContent.contentText,
          contentUrl: editingContent.contentUrl,
          displayOrder: editingContent.displayOrder,
          estimatedMinutes: editingContent.estimatedMinutes,
        });
      }
      await loadModule();
    } catch (err) {
      setContentDialogError(err instanceof Error ? err.message : t('moduleBuilder.failedToSave'));
      return;
    } finally {
      setSaving(false);
    }

    setContentDialogOpen(false);
    setEditingContent(null);
    setCurrentContentIndex(-1);
  };

  const handleDeleteContent = async (index: number) => {
    const contentItem = module.content?.[index];
    if (module.id && contentItem?.id && contentItem.id > 0) {
      try {
        setSaving(true);
        await trainingClient.deleteSection(module.id, contentItem.id);
        await loadModule();
        return;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to delete section');
        return;
      } finally {
        setSaving(false);
      }
    }
    // New module or unsaved section – remove from local state only.
    const content = module.content?.filter((_, i) => i !== index) || [];
    setModule({ ...module, content });
  };

  const handleAddQuiz = () => {
    setQuizDialogError(null);
    setEditingQuiz({
      id: 0,
      title: '',
      description: '',
      passingScore: module.passingScore,
      questions: [],
    } as unknown as TrainingModuleQuizDto);
    setCurrentQuizIndex(-1);
    setQuizDialogOpen(true);
  };

  const handleSaveQuiz = async () => {
    if (!editingQuiz?.title.trim()) {
      setQuizDialogError(t('moduleBuilder.quizTitleRequired'));
      return;
    }

    setQuizDialogError(null);

    try {
      setSaving(true);
      const isExisting = editingQuiz.id && editingQuiz.id > 0;
      const buildQuestions = (questions: typeof editingQuiz.questions) =>
        (questions || []).map(qq => ({
          id: qq.id > 0 ? qq.id : undefined,
          questionText: qq.questionText,
          questionType: qq.questionType,
          points: qq.points,
          displayOrder: qq.displayOrder,
          answers: (qq.answers || []).map(a => ({
            id: a.id > 0 ? a.id : undefined,
            answerText: a.answerText,
            isCorrect: a.isCorrect,
            displayOrder: a.displayOrder,
          })),
        }));

      if (isExisting) {
        await trainingClient.updateQuiz(module.id!, editingQuiz.id, {
          title: editingQuiz.title,
          description: editingQuiz.description ?? null,
          passingScore: editingQuiz.passingScore,
          questions: buildQuestions(editingQuiz.questions),
        });
      } else {
        await trainingClient.createQuiz(module.id!, {
          title: editingQuiz.title,
          description: editingQuiz.description ?? null,
          passingScore: editingQuiz.passingScore,
          questions: buildQuestions(editingQuiz.questions),
        });
      }
      await loadModule();
    } catch (err) {
      setQuizDialogError(err instanceof Error ? err.message : t('moduleBuilder.failedToSave'));
      return;
    } finally {
      setSaving(false);
    }

    setQuizDialogOpen(false);
    setEditingQuiz(null);
    setCurrentQuizIndex(-1);
  };

  const handleDeleteQuiz = async (index: number) => {
    const quizItem = module.quizzes?.[index];
    if (module.id && quizItem?.id && quizItem.id > 0) {
      try {
        setSaving(true);
        await trainingClient.deleteQuiz(module.id, quizItem.id);
        await loadModule();
        return;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to delete quiz');
        return;
      } finally {
        setSaving(false);
      }
    }
    // New module or unsaved quiz – remove from local state only.
    const quizzes = module.quizzes?.filter((_, i) => i !== index) || [];
    setModule({ ...module, quizzes });
  };

  const handleAddQuestion = (quizIndex: number) => {
    setQuestionDialogError(null);
    setCurrentQuizIndex(quizIndex);
    setEditingQuestion({
      id: 0,
      quizId: 0,
      questionText: '',
      questionType: QuestionType.MultipleChoice,
      points: 10,
      displayOrder: module.quizzes![quizIndex].questions?.length || 0,
      answers: [],
    } as unknown as TrainingQuizQuestionDto);
    setQuestionDialogOpen(true);
  };

  const handleSaveQuestion = () => {
    if (!editingQuestion?.questionText.trim()) {
      setQuestionDialogError(t('moduleBuilder.questionTextRequired'));
      return;
    }
    const hasCorrectAnswer = editingQuestion?.answers?.some(answer => answer.isCorrect);
    if (!hasCorrectAnswer) {
      setQuestionDialogError(t('moduleBuilder.selectCorrectAnswer'));
      return;
    }

    setQuestionDialogError(null);

    if (currentQuizIndex === -1 && editingQuiz) {
      const questions = [...(editingQuiz.questions || [])];

      const existingIndex = questions.findIndex(q =>
        editingQuestion.id && editingQuestion.id > 0 ? q.id === editingQuestion.id : false,
      );

      if (existingIndex >= 0 && editingQuestion.id && editingQuestion.id > 0) {

        questions[existingIndex] = editingQuestion;
      } else {
        const newQuestion = {
          ...editingQuestion,
          displayOrder: questions.length,
        };
        questions.push(newQuestion);
      }

      setEditingQuiz({ ...editingQuiz, questions });
      setQuestionDialogOpen(false);
      setEditingQuestion(null);
      return;
    }

    const quizzes = [...(module.quizzes || [])];

    if (currentQuizIndex < 0 || currentQuizIndex >= quizzes.length) {
      return;
    }

    const questions = [...(quizzes[currentQuizIndex].questions || [])];

    const existingIndex = questions.findIndex(q =>
      editingQuestion.id && editingQuestion.id > 0 ? q.id === editingQuestion.id : false,
    );

    if (existingIndex >= 0 && editingQuestion.id && editingQuestion.id > 0) {

      questions[existingIndex] = editingQuestion;
    } else {
      const newQuestion = {
        ...editingQuestion,
        displayOrder: questions.length,
      };
      questions.push(newQuestion);
    }

    quizzes[currentQuizIndex].questions = questions;
    setModule({ ...module, quizzes });
    setQuestionDialogOpen(false);
    setEditingQuestion(null);
  };


  const handleEditContent = async (index: number) => {
    setContentDialogError(null);
    setCurrentContentIndex(index);
    const contentItem = module.content![index];

    // Open the dialog immediately — show spinner while fetching full section detail
    setEditingContent(contentItem);
    setContentDialogOpen(true);

    // If this content was already saved (has a real id) and the module is persisted,
    // fetch full details (including contentText) from the API.
    if (module.id && contentItem.id) {
      try {
        setContentLoading(true);
        const detail = await trainingClient.getSectionById(module.id, contentItem.id);
        if (detail) {
          setOriginalContentSize(detail.contentText?.length ?? 0);
          setEditingContent(detail);
        }
      } catch {
        setContentDialogError(t('moduleBuilder.failedToLoadSection'));
      } finally {
        setContentLoading(false);
      }
    }
  };

  const handleEditQuiz = (index: number) => {
    setCurrentQuizIndex(index);
    setEditingQuiz(module.quizzes![index]);
    setQuizDialogOpen(true);
  };

  const handleEditQuestion = (questionIndex: number) => {
    setQuestionDialogError(null);
    const question = editingQuiz!.questions![questionIndex];
    setEditingQuestion(question);
    setQuestionDialogOpen(true);
  };

  const handleDeleteQuestion = (questionIndex: number) => {
    const questions = editingQuiz!.questions?.filter((_, i) => i !== questionIndex) || [];
    setEditingQuiz({ ...editingQuiz!, questions });
  };

  const handleAddAnswer = () => {
    const answers = [...(editingQuestion?.answers || [])];
    answers.push({
      id: 0,
      answerText: '',
      isCorrect: false,
      displayOrder: answers.length,
    } as unknown as TrainingQuizAnswerDto);
    setEditingQuestion({ ...editingQuestion!, answers });
  };

  const handleUpdateAnswer = (index: number, field: keyof TrainingQuizAnswerDto, value: string | boolean) => {
    const answers = [...(editingQuestion?.answers || [])];
    answers[index] = { ...answers[index], [field]: value };
    setEditingQuestion({ ...editingQuestion!, answers });
  };

  const handleDeleteAnswer = (index: number) => {
    const answers = editingQuestion?.answers?.filter((_, i) => i !== index) || [];
    setEditingQuestion({ ...editingQuestion!, answers });
  };

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleContentDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const content = module.content || [];
      const oldIndex = content.findIndex((_, i) => `content-${i}` === active.id);
      const newIndex = content.findIndex((_, i) => `content-${i}` === over.id);

      const reordered = arrayMove(content, oldIndex, newIndex).map((item, idx) => ({
        ...item,
        displayOrder: idx,
      }));

      setModule({ ...module, content: reordered });
    }
  };

  const handleQuestionDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const questions = editingQuiz?.questions || [];
      const oldIndex = questions.findIndex((_, i) => `question-${i}` === active.id);
      const newIndex = questions.findIndex((_, i) => `question-${i}` === over.id);

      const reordered = arrayMove(questions, oldIndex, newIndex).map((item, idx) => ({
        ...item,
        displayOrder: idx,
      }));

      setEditingQuiz({ ...editingQuiz!, questions: reordered });
    }
  };

  // Sortable Content Item Component
  const SortableContentItem: React.FC<{
    content: TrainingModuleContentDto;
    index: number;
  }> = ({ content, index }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: `content-${index}` });

    const draggableStyle = {
      transform: CSS.Transform.toString(transform),
      transition,
      zIndex: isDragging ? 1200 : 'auto',
      position: 'relative' as const,
    };

    const sectionStyle = {
      p: 5,
      backgroundColor: 'white',
      boxShadow: isDragging ? `0 8px 20px ${theme.palette.custom.gray[300]}` : undefined,
    };

    return (
      <Box
        ref={setNodeRef}
        style={draggableStyle}
      >
        <Section style={sectionStyle}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
              <DragIndicatorIcon
                {...attributes}
                {...listeners}
                sx={{ cursor: 'grab', color: 'text.secondary' }}
              />
              <Box>
                <Typography sx={{ fontSize: '1rem', fontWeight: 500 }}>
                  {content.title}
                </Typography>
                <Typography
                  sx={{ fontSize: '0.875rem' }}
                  color="text.secondary"
                >
                  {content.contentType === 'text' ? t('moduleBuilder.contentTypeText') :
                    content.contentType === 'video' ? t('moduleBuilder.contentTypeVideo') :
                      content.contentType === 'document' ? t('moduleBuilder.contentTypeDocument') : t('moduleBuilder.contentTypeLink')} • {content.estimatedMinutes || 0} min
                </Typography>
              </Box>
            </Box>
            <Box>
              <IconButton
                size="small"
                onClick={() => handleEditContent(index)}
                title={t('moduleBuilder.editContent')}
              >
                <EditIcon />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => handleDeleteContent(index)}
                color="error"
                title={t('moduleBuilder.deleteContent')}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          </Box>
        </Section>
      </Box>
    );
  };

  // Sortable Question Item Component
  const SortableQuestionItem: React.FC<{
    question: TrainingQuizQuestionDto;
    index: number;
  }> = ({ question, index }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: `question-${index}` });

    const draggableStyle = {
      transform: CSS.Transform.toString(transform),
      transition,
      zIndex: isDragging ? 1200 : 'auto',
      position: 'relative' as const,
    };

    return (
      <Box
        ref={setNodeRef}
        style={draggableStyle}
      >
        <Section
          style={{
            p: 5, mb: 3,
            backgroundColor: 'white',
            boxShadow: isDragging ? `0 8px 20px ${theme.palette.custom.gray[300]}` : undefined,
          }}
        >
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                <DragIndicatorIcon
                  {...attributes}
                  {...listeners}
                  sx={{ cursor: 'grab', color: 'text.secondary', mt: 0.5 }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography fontWeight={500}
                    sx={{ mb: 0.5 }}>
                    {question.questionText}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      label={`${question.points} ${t('moduleBuilder.points')}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                    <Chip
                      label={`${question.answers?.length || 0} ${t('moduleBuilder.answerOptions')}`}
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      label={`${question.answers?.filter(a => a.isCorrect).length || 0} ${t('moduleBuilder.correct')}`}
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  </Box>
                </Box>
              </Box>
              <Box>
                <IconButton
                  size="small"
                  onClick={() => handleEditQuestion(index)}
                  title={t('moduleBuilder.editQuestion')}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleDeleteQuestion(index)}
                  color="error"
                  title={t('moduleBuilder.deleteQuestion')}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Section>
      </Box>
    );
  };

  return (
    <PageArea>
      {/* Header */}
      <Stack
        width='100%'
        spacing={2}
        direction={isMobile ? 'column' : 'row'}
        sx={{ justifyContent: 'space-between', mb: 3 }}
      >
        <PageTitle subTitle={t('moduleBuilder.subtitle')}>
          {moduleId ? t('moduleBuilder.editTitle') : t('moduleBuilder.createTitle')}
        </PageTitle>
        <Stack direction="row"
          spacing={2}>
          <Button
            variant="secondary"
            size="lg"
            startIcon={<SvgIcon><ArrowLeft /></SvgIcon>}
            onClick={() => navigate('/compliance/training/modules')}
          >
            {t('moduleBuilder.back')}
          </Button>
          <Button
            variant="secondary"
            size="lg"
            startIcon={<SaveIcon />}
            onClick={handleSaveModule}
            disabled={saving}
          >
            {t('moduleBuilder.saveDraft')}
          </Button>
          {moduleId && (
            <Button
              variant="primary"
              size="lg"
              startIcon={<PublishIcon />}
              onClick={handlePublish}
              disabled={saving}
            >
              {t('moduleBuilder.publish')}
            </Button>
          )}
        </Stack>
      </Stack>

      {/* Alerts */}
      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{ mb: 2 }}
        >
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          onClose={() => setSuccess(null)}
          sx={{ mb: 2 }}
        >
          {success}
        </Alert>
      )}

      {/* Module Details */}
      <Section style={{ p: 5, mb: 5, pb: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ fontSize: '1rem', fontWeight: 500, mb: 5 }}>
            {t('moduleBuilder.moduleDetails')}
          </Box>
          <FormControlLabel
            sx={{ flexDirection: 'row-reverse', mr: 0 }}
            control={
              <Switch
                checked={module.isActive}
                onChange={(e) => setModule({ ...module, isActive: e.target.checked })}
              />
            }
            label="Active"
          />
        </Box>
        <TextField
          fullWidth
          label={t('moduleBuilder.moduleName')}
          value={module.name}
          onChange={(e) => setModule({ ...module, name: e.target.value })}
          required
        />
        <TextField
          fullWidth
          select
          aria-label="Franchise"
          label={t('moduleBuilder.franchise')}
          value={module.franchiseIds || []}
          onChange={(e) => {
            const value = e.target.value as unknown as number[];
            if (value.includes(-1)) {
              const allIds = franchises.map(f => f.id);
              const isAllSelected = (module.franchiseIds || []).length === franchises.length;
              setModule({ ...module, franchiseIds: isAllSelected ? [] : allIds, categoryId: 0, categoryName: '' });
            } else {
              setModule({ ...module, franchiseIds: value, categoryId: 0, categoryName: '' });
            }
          }}
          required
          SelectProps={{
            multiple: true,
            renderValue: (selected) => {
              const ids = selected as number[];
              if (ids.length === 0) return '';
              if (ids.length === franchises.length) return t('trainingCategories.allFranchises');
              return ids.map(id => franchises.find(f => f.id === id)?.name || id).join(', ');
            },
          }}
        >
          <MenuItem value={-1}>
            <Checkbox
              checked={(module.franchiseIds || []).length === franchises.length && franchises.length > 0}
              indeterminate={(module.franchiseIds || []).length > 0 && (module.franchiseIds || []).length < franchises.length}
            />
            <ListItemText primary={t('trainingCategories.selectAll')} />
          </MenuItem>
          {franchises.map((franchise) => (
            <MenuItem key={franchise.id}
              value={franchise.id}>
              <Checkbox checked={(module.franchiseIds || []).includes(franchise.id)} />
              <ListItemText primary={franchise.name} />
            </MenuItem>
          ))}
        </TextField>
        <TextField
          fullWidth
          select
          aria-label="Training Category"
          label={t('moduleBuilder.trainingCategory')}
          value={module.categoryId || ''}
          onChange={(e) => {
            const categoryId = parseInt(e.target.value as string);
            const category = categories.find(c => c.id === categoryId);
            setModule({
              ...module,
              categoryId,
              categoryName: category?.name || '',
            });
          }}
          required
        >
          <MenuItem value=""><em>&nbsp;</em></MenuItem>
          {categories.map((category) => (
            <MenuItem key={category.id}
              value={category.id}>
              {category.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          fullWidth
          multiline
          rows={3}
          label={t('moduleBuilder.description')}
          value={module.description || ''}
          onChange={(e) => setModule({ ...module, description: e.target.value })}
        />
        <Box
          display="flex"
          gap={2}
          mb={2}
        >
          <TextField
            fullWidth
            type="number"
            label={t('moduleBuilder.durationMinutes')}
            value={module.durationMinutes}
            onChange={(e) => setModule({ ...module, durationMinutes: parseInt(e.target.value) })}
          />
          <TextField
            fullWidth
            type="number"
            label={t('moduleBuilder.passingScore')}
            value={module.passingScore}
            onChange={(e) => setModule({ ...module, passingScore: parseInt(e.target.value) })}
          />
          <TextField
            fullWidth
            type="number"
            label={t('moduleBuilder.validityDays')}
            value={module.validityDays}
            onChange={(e) => setModule({ ...module, validityDays: parseInt(e.target.value) })}
          />
        </Box>

      </Section>

      {/* Tabs – only available once the module has been saved */}
      {moduleId ? (
        <Box sx={{ ml: 5, mr: 5, mb: -1 }}>
          <Tabs
            value={currentTab}
            onChange={(_, newValue) => setCurrentTab(newValue)}
          >
            <Tab label={`${t('moduleBuilder.contentTab')} (${module.content?.length || 0})`} />
            <Tab label={`${t('moduleBuilder.quizzesTab')} (${module.quizzes?.length || 0})`} />
          </Tabs>
        </Box>
      ) : (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
        >
          {t('moduleBuilder.saveFirstToAddContent', 'Save the module first to add content and quizzes.')}
        </Alert>
      )}
      {moduleId && <Section style={{ p: 5, mb: 5 }}>
        {/* Content Tab */}
        {currentTab === 0 && (
          <Box>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={5}
            >
              <Box sx={{ fontSize: '1rem', fontWeight: 500 }}>
                {t('moduleBuilder.trainingContent')}
              </Box>
              <Button
                variant="primary"
                size="lg"
                startIcon={<AddIcon />}
                onClick={handleAddContent}
              >
                {t('moduleBuilder.addContent')}
              </Button>
            </Box>

            {module.content && module.content.length > 0 ? (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleContentDragEnd}
              >
                <SortableContext
                  items={module.content.map((_, i) => `content-${i}`)}
                  strategy={verticalListSortingStrategy}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {module.content.map((content, index) => (
                      <SortableContentItem
                        key={index}
                        content={content}
                        index={index}
                      />
                    ))}
                  </Box>
                </SortableContext>
              </DndContext>
            ) : (
              <Box
                sx={{
                  py: 4,
                  px: 2,
                  color: theme.palette.custom.gray[600],
                  textAlign: 'center',
                  backgroundColor: theme.palette.custom.gray[50],
                  borderRadius: 1,
                }}
              >
                {t('moduleBuilder.noContent')}
              </Box>
            )}
          </Box>
        )}

        {/* Quizzes Tab */}
        {currentTab === 1 && (
          <Box>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={5}
            >
              <Box sx={{ fontSize: '1rem', fontWeight: 500 }}>
                {t('moduleBuilder.quizzes')}
              </Box>
              <Button
                variant="primary"
                size="lg"
                startIcon={<AddIcon />}
                onClick={handleAddQuiz}
              >
                {t('moduleBuilder.addQuiz')}
              </Button>
            </Box>

            {module.quizzes && module.quizzes.length > 0 ? (
              <>
                {module.quizzes.map((quiz, quizIndex) => (
                  <Section key={quizIndex}
                    style={{ p: 5, mb: 3, boxShadow: 'none' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Stack>
                        <Typography sx={{ fontSize: '1rem', fontWeight: 500 }}>
                          {quiz.title}
                        </Typography>
                        <Typography
                          sx={{ fontSize: '0.875rem' }}
                          color="text.secondary"
                        >
                          {t('moduleBuilder.quizSummary', { count: quiz.questions?.length || 0, score: quiz.passingScore ?? 70 })}
                        </Typography>
                      </Stack>
                      <Box>
                        <IconButton
                          size="small"
                          onClick={() => handleAddQuestion(quizIndex)}
                        >
                          <AddIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditingQuiz(quiz);
                            setCurrentQuizIndex(quizIndex);
                            setQuizDialogOpen(true);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteQuiz(quizIndex)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  </Section>
                ))}
              </>
            ) : (
              <Box
                sx={{
                  py: 4,
                  px: 2,
                  color: theme.palette.custom.gray[600],
                  textAlign: 'center',
                  backgroundColor: theme.palette.custom.gray[50],
                  borderRadius: 1,
                }}
              >
                {t('moduleBuilder.noQuizzes')}
              </Box>
            )}
          </Box>
        )}
      </Section>}

      {/* Content Dialog */}
      <Dialog
        open={contentDialogOpen}
        onClose={() => {
          setContentDialogOpen(false);
          setContentDialogError(null);
          setCurrentContentIndex(-1);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {currentContentIndex >= 0 ? t('moduleBuilder.editContentTitle') : t('moduleBuilder.addContentTitle')}
        </DialogTitle>
        <DialogContent sx={{
          '& .ql-container': { direction: 'ltr' },
          '& .ql-editor': { direction: 'ltr', textAlign: 'left' },
          '& .ql-editor video': { pointerEvents: 'all' },
          '& .ql-tooltip': {
            left: '0 !important',
            top: 'auto !important',
            position: 'absolute !important',
            transform: 'none !important',
          },
        }}>
          {contentLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {contentDialogError && (
                <Alert severity="error"
                  sx={{ mt: 2, mb: 2 }}>
                  {contentDialogError}
                </Alert>
              )}
              <TextField
                fullWidth
                label={t('moduleBuilder.contentTitle')}
                value={editingContent?.title || ''}
                onChange={(e) => setEditingContent({ ...editingContent!, title: e.target.value })}
                sx={{ mt: 2 }}
                required
              />
              <TextField
                fullWidth
                type="number"
                label={t('moduleBuilder.estimatedMinutes')}
                value={editingContent?.estimatedMinutes || 0}
                onChange={(e) => setEditingContent({ ...editingContent!, estimatedMinutes: parseInt(e.target.value) || 0 })}
                inputProps={{ min: 0 }}
              />
              <Box sx={{ mb: 2, position: 'relative' }}
                className="quill-container">
                <Typography sx={{ mb: 1, fontWeight: 500 }}>
                  {t('moduleBuilder.contentText')}
                </Typography>
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  bounds=".quill-container"
                  value={editingContent?.contentText || ''}
                  onChange={(value) => setEditingContent({ ...editingContent!, contentText: value })}
                  modules={quillModules}
                  style={{ height: '300px', marginBottom: '50px' }}
                />
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="secondary"
            size="lg"
            onClick={() => { setContentDialogOpen(false); setContentDialogError(null); setCurrentContentIndex(-1); }}
            disabled={saving || contentLoading}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            size="lg"
            onClick={handleSaveContent}
            disabled={saving || contentLoading}
            startIcon={saving ? <CircularProgress size={16}
              color="inherit" /> : undefined}
          >
            {saving ? t('common.saving') : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Quiz Dialog */}
      <Dialog
        open={quizDialogOpen}
        onClose={() => {
          setQuizDialogOpen(false);
          setQuizDialogError(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {currentQuizIndex >= 0 ? t('moduleBuilder.editQuizTitle') : t('moduleBuilder.addQuizTitle')}
        </DialogTitle>
        <DialogContent>
          {quizDialogError && (
            <Alert severity="error"
              sx={{ mt: 2, mb: 2 }}>
              {quizDialogError}
            </Alert>
          )}
          <TextField
            fullWidth
            label={t('moduleBuilder.quizTitle')}
            value={editingQuiz?.title || ''}
            onChange={(e) => setEditingQuiz({ ...editingQuiz!, title: e.target.value })}
            sx={{ mt: 2 }}
            required
          />
          <TextField
            fullWidth
            multiline
            rows={3}
            label={t('moduleBuilder.description')}
            value={editingQuiz?.description || ''}
            onChange={(e) => setEditingQuiz({ ...editingQuiz!, description: e.target.value })}
          />
          <TextField
            fullWidth
            type="number"
            label={t('moduleBuilder.passingScore')}
            value={editingQuiz?.passingScore}
            onChange={(e) => {
              const value = parseInt(e.target.value);
              const clampedValue = Math.min(Math.max(value || 0, 0), 100);
              setEditingQuiz({ ...editingQuiz!, passingScore: clampedValue });
            }}
            inputProps={{ min: 0, max: 100 }}
          />

          {/* Questions Section */}
          <Box sx={{ mt: 3 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Typography sx={{ fontSize: '1rem', fontWeight: 500 }}>
                {`${t('moduleBuilder.questions')} (${editingQuiz?.questions?.length || 0})`}
              </Typography>
              <Button
                size="lg"
                startIcon={<AddIcon />}
                onClick={() => {
                  setEditingQuestion({
                    id: 0,
                    quizId: editingQuiz?.id || 0,
                    questionText: '',
                    questionType: QuestionType.MultipleChoice,
                    points: 1,
                    displayOrder: editingQuiz?.questions?.length || 0,
                    answers: [],
                  });
                  setQuestionDialogOpen(true);
                }}
              >
                {t('moduleBuilder.addQuestion')}
              </Button>
            </Box>

            {editingQuiz?.questions && editingQuiz.questions.length > 0 ? (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleQuestionDragEnd}
              >
                <SortableContext
                  items={editingQuiz.questions.map((_, i) => `question-${i}`)}
                  strategy={verticalListSortingStrategy}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {editingQuiz.questions.map((question, index) => (
                      <SortableQuestionItem
                        key={index}
                        question={question}
                        index={index}
                      />
                    ))}
                  </Box>
                </SortableContext>
              </DndContext>
            ) : (
              <Box
                sx={{
                  py: 4,
                  px: 2,
                  color: theme.palette.custom.gray[600],
                  textAlign: 'center',
                  backgroundColor: theme.palette.custom.gray[50],
                  borderRadius: 1,
                }}
              >
                {t('moduleBuilder.noQuestions')}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="secondary"
            size="lg"
            onClick={() => setQuizDialogOpen(false)}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            size="lg"
            onClick={handleSaveQuiz}
          >
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Question Dialog */}
      <Dialog
        open={questionDialogOpen}
        onClose={() => {
          setQuestionDialogOpen(false);
          setQuestionDialogError(null);
        }}
        maxWidth="md"
        fullWidth
        key={editingQuestion?.id || 'new-question'}
      >
        <DialogTitle>
          {editingQuestion?.id ? t('moduleBuilder.editQuestionTitle') : t('moduleBuilder.addQuestionTitle')}
        </DialogTitle>
        <DialogContent>
          {questionDialogError && (
            <Alert severity="error"
              sx={{ mt: 2, mb: 2 }}>
              {questionDialogError}
            </Alert>
          )}
          <TextField
            fullWidth
            label={t('moduleBuilder.questionText')}
            value={editingQuestion?.questionText || ''}
            onChange={(e) => setEditingQuestion({ ...editingQuestion!, questionText: e.target.value })}
            sx={{ mt: 2 }}
            required
            multiline
            rows={2}
          />
          <TextField
            fullWidth
            type="number"
            label={t('moduleBuilder.pointsLabel')}
            value={editingQuestion?.points || 1}
            onChange={(e) => setEditingQuestion({ ...editingQuestion!, points: parseInt(e.target.value) || 1 })}
            inputProps={{ min: 1 }}
          />

          {/* Answers Section */}
          <Box sx={{ mt: 3 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1,
              }}
            >
              <Typography sx={{ fontSize: '1rem', fontWeight: 500 }}>
                {`${t('moduleBuilder.answerOptions')} (${editingQuestion?.answers?.length || 0})`}
              </Typography>
              <Button
                size="lg"
                startIcon={<AddIcon />}
                onClick={handleAddAnswer}
              >
                {t('moduleBuilder.addAnswer')}
              </Button>
            </Box>
            <Typography
              sx={{ fontSize: '0.875rem', color: 'text.secondary', mb: 2 }}
            >
              {t('moduleBuilder.answerOptionsHelper')}
            </Typography>

            {editingQuestion?.answers && editingQuestion.answers.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {editingQuestion.answers.map((answer, index) => (
                  <Box
                    key={index}
                    sx={{
                      p: 3,
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                      <Checkbox
                        checked={answer.isCorrect}
                        onChange={(e) => handleUpdateAnswer(index, 'isCorrect', e.target.checked)}
                        sx={{ mt: 1 }}
                        color="success"
                      />
                      <TextField
                        fullWidth
                        sx={{ mb: 0 }}
                        label={`${t('moduleBuilder.answer')} ${index + 1}`}
                        value={answer.answerText}
                        onChange={(e) => handleUpdateAnswer(index, 'answerText', e.target.value)}
                        placeholder={t('moduleBuilder.answerPlaceholder')}
                      />
                      {answer.isCorrect && (
                        <Chip
                          label={t('moduleBuilder.correctAnswer')}
                          color="success"
                          size="small"
                          sx={{ mt: 1 }}
                        />
                      )}
                      <IconButton
                        onClick={() => handleDeleteAnswer(index)}
                        sx={{ mt: 1 }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>

                  </Box>
                ))}
              </Box>
            ) : (
              <Box
                sx={{
                  py: 4,
                  px: 2,
                  color: theme.palette.custom.gray[600],
                  textAlign: 'center',
                  backgroundColor: theme.palette.custom.gray[50],
                  borderRadius: 1,
                }}
              >
                {t('moduleBuilder.noAnswers')}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="secondary"
            size="lg"
            onClick={() => setQuestionDialogOpen(false)}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            size="lg"
            onClick={handleSaveQuestion}
          >
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </PageArea >
  );
};

export default TrainingModuleBuilderPage;
